import {
  importPrivateKey,
  signEnvelope,
} from '../../shared/sign';
import { canonicalClaim } from '../../shared/crypto';
import {
  parseAuthMe,
  isValidDeviceTag,
  MAX_DEVICE_TAG_LEN,
} from '../../shared/wire';
import { $, qsa } from './dom';
import { showToast } from './toast';
import { deviceDisplayName } from './animals';
import { renderDevicePanel } from './device-panel';
import { renderDeviceHeader, renderSessions } from './sidebar';
import { setGroupClaimProbe } from './group-claim';
import {
  getAuthUserId, setAuthUserId,
  getMyClaims, setMyClaims,
} from '../state/auth-state';
import { MOCK } from '../state/device';
import { state } from '../state/app-state';
import { loadKeypair, loadStoredToken, loadDeviceHistory } from '../storage/persistence';
import {
  apiAuthMe,
  apiListClaims,
  apiClaimChallenge,
  apiClaimAccept,
  apiClaimRevoke,
  apiSetVisibility,
  apiSetCohortScope,
  apiSetDeviceTag,
} from '../api/claim';
import { startWorldLive } from '../map/world';
import { stopConsolePoll, clearTeam } from '../map/team-layer';

/* ─────────────────────────────────────────────
   Callbacks injected by main.ts boot() to avoid circular imports
   ───────────────────────────────────────────── */
let _refreshAuthUiCb: (() => void) | null = null;
export function setAccountRefreshAuthUiCb(fn: () => void): void { _refreshAuthUiCb = fn; }
let _openDrawerCb: ((view: string) => void) | null = null;
export function setOpenDrawerCb(fn: (view: string) => void): void { _openDrawerCb = fn; }
let _closeDrawerCb: (() => void) | null = null;
export function setCloseDrawerCb(fn: () => void): void { _closeDrawerCb = fn; }
let _showCb: ((which: string) => void) | null = null;
export function setShowCb(fn: (which: string) => void): void { _showCb = fn; }
let _activateDeviceCb: ((device: any, restored: boolean) => void) | null = null;
export function setActivateDeviceCb(fn: (device: any, restored: boolean) => void): void { _activateDeviceCb = fn; }
let _openBackupRestoreCb: (() => void) | null = null;
export function setOpenBackupRestoreCb(fn: () => void): void { _openBackupRestoreCb = fn; }
let _decodeTokenCb: ((token: string | null) => any) | null = null;
export function setDecodeTokenCb(fn: (token: string | null) => any): void { _decodeTokenCb = fn; }
let _getDrawerViewCb: (() => string) | null = null;
export function setGetDrawerViewCb(fn: () => string): void { _getDrawerViewCb = fn; }

/* ─────────────────────────────────────────────
   AUTH + CLAIM (Google sign-in, device ownership) — ADR 0014
   ───────────────────────────────────────────── */
export const GOOGLE_CLIENT_ID =
  (document.querySelector('meta[name="google-client-id"]') as HTMLMetaElement | null)?.content || '';
export let gisInited = false;

export async function refreshMyClaims(){
  if(getAuthUserId() == null){ setMyClaims(null); return; }
  try {
    const list = await apiListClaims();
    setMyClaims(new Map(list.claims.map(c => [c.device_token, { claimed_at: c.claimed_at, visibility: c.visibility, group: c.group }])));
  } catch (err) {
    console.warn('[locus] list claims failed', err);
    setMyClaims(null);
  }
}

export async function claimCurrentDevice(){
  if(!MOCK.device) return;
  const token = MOCK.device.token;
  const stored = loadKeypair(token);
  if(!stored){ showToast('no signing key for this device in this browser'); return; }
  try {
    const ch = await apiClaimChallenge(token);
    const privKey = await importPrivateKey(stored.priv);
    const timestamp = Date.now();
    const sig = await signEnvelope(privKey, canonicalClaim(token, 'claim', ch.nonce, timestamp));
    await apiClaimAccept(token, ch.nonce, sig, timestamp);
    showToast('device claimed');
    await refreshMyClaims();
    _refreshAuthUiCb?.();
  } catch (err: any) {
    if(err?.status === 409) showToast('already claimed by another account');
    else if(err?.status === 401) showToast('please sign in again');
    else if(err?.status === 404) showToast('device not found on server');
    else showToast('claim failed');
  }
}
export async function unclaimCurrentDevice(){
  if(!MOCK.device) return;
  const token = MOCK.device.token;
  try {
    await apiClaimRevoke(token);
    showToast('device un-claimed');
  } catch (err: any) {
    if(err?.status !== 404){ showToast('un-claim failed'); return; }
    // 404 = already not owned; fall through to refresh so the UI converges.
  }
  await refreshMyClaims();
  _refreshAuthUiCb?.();
}
export async function changeVisibility(visibility: string){
  if(!MOCK.device) return;
  const token = MOCK.device.token;
  try {
    const out = await apiSetVisibility(token, visibility);
    showToast('visibility: ' + out.visibility);
    const entry = getMyClaims()?.get(token);
    if(entry) entry.visibility = out.visibility;
    // The cohort-scope controls appear/disappear with `group` visibility, so
    // re-render the Device panel to reflect the new visibility.
    renderDevicePanel();
  } catch (err: any) {
    if(err?.status === 404) showToast('claim this device first');
    else if(err?.status === 401) showToast('please sign in again');
    else showToast('could not set visibility');
  }
}

// Signed cohort-scope setter — mirrors apiMintShare (key possession, no
// session). The device's ed25519 key signs canonicalCohortScope; the server
// verifies and writes devices.cohort_range / cohort_precision.
export async function changeCohortScope(){
  if(!MOCK.device) return;
  const token = MOCK.device.token;
  const precision = ($('cohort-precision-select') as HTMLSelectElement | null)?.value || 'exact';
  const range = ($('cohort-range-select') as HTMLSelectElement | null)?.value || 'all';
  try {
    const out = await apiSetCohortScope(token, { range, precision });
    showToast(`cohort scope: ${out.cohort_scope.precision} · ${out.cohort_scope.range}`);
    // Reflect the new scope locally so the badge + selects stay in sync before
    // the next poll echoes it.
    const entry = state.cohort.get(token);
    if(entry) entry.cohort_scope = out.cohort_scope;
  } catch (err: any) {
    if(err?.status === 404) showToast('device not found on server');
    else showToast('could not set cohort scope');
  }
}

// Reflect a just-set tag across the current-device render surfaces before the
// next 30 s cohort poll echoes it.
export function setCurrentDeviceTag(tag: string | null){
  if(MOCK.device) MOCK.device.tag = tag;
  const tok = MOCK.device?.token;
  const entry = tok ? state.cohort.get(tok) : undefined;
  if(entry) entry.tag = tag;
  renderDeviceHeader();          // header + drawer-active (it calls renderDrawerActive)
  if(_getDrawerViewCb?.() === 'device') renderDevicePanel();
  renderSessions();              // refreshes the "By device" list if it's the open tab
}

export async function renameCurrentDevice(){
  if(!MOCK.device) return;
  const token = MOCK.device.token;
  if(!loadKeypair(token)){ showToast('this device has no signing key in this browser'); return; }
  const input = prompt('Display name for this device (leave empty to clear)', MOCK.device.tag || '');
  if(input === null) return;     // cancelled
  const next = input.trim();
  if(next.length > MAX_DEVICE_TAG_LEN){ showToast(`name must be ${MAX_DEVICE_TAG_LEN} characters or fewer`); return; }
  if(next.length > 0 && !isValidDeviceTag(next)){ showToast('name has invalid characters'); return; }
  try {
    const out = await apiSetDeviceTag(token, next || null);
    setCurrentDeviceTag(out.tag);
    showToast(out.tag ? 'device renamed' : 'name cleared');
  } catch (err: any) {
    if(err?.status === 404) showToast('device not found on server');
    else if(err?.status === 400) showToast('invalid name');
    else showToast('could not set name');
  }
}

export async function onGoogleCredential(response: any){
  try {
    const res = await fetch('/api/v1/auth/google', {
      method: 'POST', credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ credential: response?.credential }),
    });
    if(res.status !== 200){ showToast('sign-in failed'); return; }
    setAuthUserId(parseAuthMe(await res.json()).id);
    showToast('signed in');
    await refreshMyClaims();
    _refreshAuthUiCb?.();
    // If the user signed in while sitting on the world map, re-kick its feed.
    // The world view gates on auth and calls stopWorldLive() when it sees a
    // signed-out state, so a later sign-in would otherwise leave the overlay up
    // (and the poll stopped) until the user navigates away and back.
    if($('screen-world').classList.contains('is-active')) startWorldLive();
  } catch (err) {
    console.warn('[locus] sign-in failed', err);
    showToast('sign-in failed');
  }
}
export async function onSignOut(){
  try { await fetch('/api/v1/auth/logout', { method: 'POST', credentials: 'include' }); } catch {}
  setAuthUserId(null);
  setMyClaims(null);
  setGroupClaimProbe(null);      // drop the cached group-claim state (next user re-probes)
  stopConsolePoll();
  clearTeam();                 // signing out drops the team layer (gate fails)
  showToast('signed out');
  _refreshAuthUiCb?.();
}

export function renderGisButton(){
  const target = $('gis-btn');
  if(!target) return;
  if(!GOOGLE_CLIENT_ID || GOOGLE_CLIENT_ID.startsWith('REPLACE_')){
    target.innerHTML = '<span class="acct-hint">Google sign-in not configured.</span>';
    return;
  }
  const g = (window as any).google;
  if(!g?.accounts?.id){
    // GIS script still loading — retry shortly.
    setTimeout(renderGisButton, 300);
    return;
  }
  try {
    if(!gisInited){
      g.accounts.id.initialize({ client_id: GOOGLE_CLIENT_ID, callback: onGoogleCredential });
      gisInited = true;
    }
    target.innerHTML = '';
    g.accounts.id.renderButton(target, { theme: 'outline', size: 'medium', text: 'signin_with' });
  } catch (err) {
    console.warn('[locus] GIS init failed', err);
  }
}

// Activate a claimed device by token (from the "My devices" list). Needs the
// device's signing key in this browser; otherwise it can be owned but not
// driven from here.
export function openClaimedDevice(token: string){
  _closeDrawerCb?.();
  if(token === loadStoredToken()){ _showCb?.('map'); return; }
  if(!loadKeypair(token)){ showToast('no signing key for this device in this browser'); return; }
  const dec = _decodeTokenCb?.(token);
  const entry = loadDeviceHistory().find((d: any) => d.token === token)
    || (dec ? { token, animals: dec.animals, group: dec.group, created_at: Date.now() } : null);
  if(!entry){ showToast('cannot open this device'); return; }
  _activateDeviceCb?.(entry, /*restored*/ true);
}

// "My devices" — the server's list of devices this account claims. Names are
// decoded from the (constrained) token and rendered from the byKey constant
// table, so no user-controlled HTML reaches innerHTML.
export function renderMyDevicesList(): string {
  const _mc = getMyClaims();
  if(_mc == null) return `<div class="menu-label">My devices</div><div class="acct-hint">loading…</div>`;
  if(_mc.size === 0) return `<div class="menu-label">My devices</div><div class="acct-hint">No claimed devices yet.</div>`;
  const cur = MOCK.device?.token;
  const rows = Array.from(_mc.entries()).map(([token, c]) => {
    const dec = _decodeTokenCb?.(token);
    const names = deviceDisplayName(token, dec ? dec.animals : [], dec ? dec.group : null);
    const tail = token.split('-').slice(-1)[0];
    const vis = c.visibility || '—';
    const action = token === cur
      ? `<span class="myd-cur">current</span>`
      : loadKeypair(token)
        ? `<button class="myd-open" data-token="${token}">Open</button>`
        : `<span class="acct-hint">no key here</span>`;
    // Remove (unclaim) is offered on every NON-current row — including ones with
    // no key here — so an account can be cleaned up from any browser. The current
    // device keeps its existing Forget action in the device drawer.
    const remove = token === cur
      ? ``
      : `<button class="myd-remove danger" data-token="${token}" aria-label="Remove device">Remove</button>`;
    return `<div class="myd-row"><span class="myd-name">${names}</span>` +
      `<span class="myd-vis">${vis}</span>${action}${remove}<span class="myd-tail">${tail}</span></div>`;
  }).join('');
  return `<div class="menu-label">My devices (${_mc.size})</div><div class="myd-list">${rows}</div>`;
}

// The Account sub-view: sign-in / sign-out + the cross-device "My devices"
// list. Per-device claim + visibility live on the Device sub-view now
// (renderDevicePanel), so this stays focused on the account itself.
export function renderAccountPanel(){
  const box = $('account-box');
  if(!box) return;
  if(getAuthUserId() == null){
    box.innerHTML =
      `<div class="acct-signedout"><div id="gis-btn"></div>` +
      `<span class="acct-hint">Sign in to claim a device and control its visibility.</span></div>`;
    renderGisButton();
    return;
  }
  box.innerHTML =
    `<div class="acct-signedin"><span class="acct-hint">Signed in</span>` +
    `<button class="menu-action" id="signout-btn"><span>Sign out</span><i class="ph ph-sign-out"></i></button></div>` +
    `<button class="menu-action" id="join-group-btn"><span>Join a private group…</span><i class="ph ph-sign-in"></i></button>` +
    `<button class="menu-action" id="restore-btn"><span>Restore a device from backup…</span><i class="ph ph-cloud-arrow-down"></i></button>` +
    renderMyDevicesList();
  $('signout-btn')?.addEventListener('click', onSignOut);
  $('join-group-btn')?.addEventListener('click', promptJoinPrivateGroup);
  $('restore-btn')?.addEventListener('click', () => { _openBackupRestoreCb?.(); });
  qsa('.myd-open', box).forEach((b: any) => b.addEventListener('click', () => openClaimedDevice(b.dataset.token)));
  qsa('.myd-remove', box).forEach((b: any) => b.addEventListener('click', () => removeClaimedDevice(b.dataset.token)));
}

// Remove (unclaim) a device from the account, from the "My devices" list. Uses
// the existing claims/revoke primitive; no local key teardown (the row may have
// no key in this browser). Refreshes the list in place.
export async function removeClaimedDevice(token: string){
  try {
    await apiClaimRevoke(token);
    showToast('device removed');
  } catch (err: any) {
    // revoke shares the 10/min/user `rl:claim` bucket — a quick run down the list
    // trips a 429, which must read as "slow down", not a generic failure.
    if(err?.status === 404) showToast('you do not own this device');
    else if(err?.status === 429) showToast('slow down — rate limited');
    else showToast('could not remove device');
    return;
  }
  await refreshMyClaims();
  renderAccountPanel();
}

// "Join a private group…" — the web counterpart to the Android Join screen and
// the entry point symptom C was missing. Purely a normaliser: it accepts a
// pasted full invite link OR a bare <g1>-<g2>/<token>, then sets the hash so the
// SINGLE entry path (routeJoinGroupHash) does all parse/validate/create work —
// no parallel join logic here (mirrors how the share "Open" button is the only
// way into #/s/).
export function promptJoinPrivateGroup(){
  if(getAuthUserId() == null){
    showToast('sign in to join a private group');
    _openDrawerCb?.('account');
    return;
  }
  const raw = (prompt('Paste an invite link (or <group>/<token>)') || '').trim();
  if(!raw) return;
  const i = raw.indexOf('#/join-group/');
  const rest = i >= 0 ? raw.slice(i + '#/join-group/'.length) : raw;
  if(!rest || rest.indexOf('/') < 0){ showToast('not a valid invite link'); return; }
  _closeDrawerCb?.();
  location.hash = '#/join-group/' + rest;
}

export async function initAuth(){
  try { setAuthUserId(await apiAuthMe()); } catch { setAuthUserId(null); }
  await refreshMyClaims();
  _refreshAuthUiCb?.();
}
